// What a page can see of the browser it runs in, for the CUS-904 stall
// telemetry. Nothing here can see extensions, policies or a local proxy.

interface UserAgentData {
  brands?: { brand: string; version: string }[]
  getHighEntropyValues?: (
    hints: string[],
  ) => Promise<{ platformVersion?: string }>
}

const TAB_LOCK_PREFIX = 'mango-webapp-tab:'

let platformVersion: string | null = null

const userAgentData = (): UserAgentData | undefined =>
  (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData

export interface BrowserContext {
  /** Client-hint brands, e.g. "Chromium|Google Chrome"; null when unsupported. */
  brands: string | null
  /** Brave ships Chrome's exact user agent string; this is the only tell. */
  brave: boolean
  /** Installed-PWA window rather than a browser tab. */
  standalone: boolean
  /** Windows 10 reports 0.x, Windows 11 reports 13+ (major). */
  platformVersion: string | null
}

export const getBrowserContext = (): BrowserContext => {
  const brands =
    userAgentData()
      ?.brands?.map((b) => b.brand)
      .filter((b) => !/not.a.brand/i.test(b))
      .join('|') ?? null
  return {
    brands: brands === '' ? null : brands,
    brave:
      typeof (navigator as Navigator & { brave?: { isBrave?: unknown } }).brave
        ?.isBrave === 'function',
    standalone:
      typeof matchMedia === 'function' &&
      matchMedia('(display-mode: standalone)').matches,
    platformVersion,
  }
}

export const loadPlatformVersion = async (): Promise<void> => {
  try {
    const values = await userAgentData()?.getHighEntropyValues?.([
      'platformVersion',
    ])
    platformVersion = values?.platformVersion ?? null
  } catch {
    platformVersion = null
  }
}

/** True when Chrome's Memory Saver discarded this tab and it reloaded. */
export const isDocumentDiscarded = (): boolean =>
  (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true

// One shared Web Lock per tab, held for the tab's lifetime, so any tab can
// count the app's open tabs with locks.query(). A held Web Lock also blocks
// Chrome from freezing the tab (see CUS-904); removing this changes that.
export const holdTabLock = (): void => {
  const locks = navigator.locks
  if (!locks?.request) {
    return
  }
  const name = `${TAB_LOCK_PREFIX}${
    typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  }`
  void locks
    .request(name, { mode: 'shared' }, () => new Promise<void>(() => {}))
    .catch(() => {})
}

export const countAppTabs = async (): Promise<number | null> => {
  const locks = navigator.locks
  if (!locks?.query) {
    return null
  }
  try {
    const { held } = await locks.query()
    return (held ?? []).filter((lock) => lock.name?.startsWith(TAB_LOCK_PREFIX))
      .length
  } catch {
    return null
  }
}

export const resetBrowserContextForTests = (): void => {
  platformVersion = null
}
